import React from 'react';
import { Box, Typography } from '@mui/material';

const FancyBackdropLoader = ({ message = 'Loading...' }) => {
  return (
    <Box
      className="fancy-backdrop-loader-container"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        position: 'relative',
        isolation: 'isolate', // Additional CSS isolation
      }}
    >
      {/* Main loader container */}
      <Box
        sx={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Outer rotating ring */}
        <Box
          sx={{
            width: '80px',
            height: '80px',
            border: '3px solid rgba(255, 255, 255, 0.2)',
            borderTop: '3px solid #fff',
            borderRadius: '50%',
            animation: 'fancyBackdropSpin 1s linear infinite',
            position: 'absolute',
          }}
        />

        {/* Middle pulsing ring */}
        <Box
          sx={{
            width: '60px',
            height: '60px',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderRight: '2px solid #4ECDC4',
            borderRadius: '50%',
            animation: 'fancyBackdropSpin 1.5s linear infinite reverse',
            position: 'absolute',
          }}
        />

        {/* Inner rotating dots */}
        <Box
          sx={{
            width: '40px',
            height: '40px',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: '8px',
              height: '8px',
              background: '#FFD700',
              borderRadius: '50%',
              transform: 'translate(-50%, -50%)',
              animation: 'fancyBackdropOrbit 2s linear infinite',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: '6px',
              height: '6px',
              background: '#FF6B6B',
              borderRadius: '50%',
              transform: 'translate(-50%, -50%)',
              animation: 'fancyBackdropOrbit 2s linear infinite 0.5s',
            },
          }}
        />

        {/* Center pulsing dot */}
        <Box
          sx={{
            width: '12px',
            height: '12px',
            background: 'linear-gradient(45deg, #FFD700, #FF6B6B)',
            borderRadius: '50%',
            animation: 'fancyBackdropPulse 1.5s ease-in-out infinite',
            boxShadow: '0 0 20px rgba(255, 215, 0, 0.6)',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </Box>

      {/* Loading text */}
      <Typography
        variant="h6"
        sx={{
          color: '#fff',
          fontWeight: 500,
          textAlign: 'center',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
          animation: 'fancyBackdropFadeInOut 2s ease-in-out infinite',
        }}
      >
        {message}
      </Typography>

      {/* Animated progress dots */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          '& > div': {
            width: '6px',
            height: '6px',
            background: '#fff',
            borderRadius: '50%',
            animation: 'fancyBackdropBounce 1.4s ease-in-out infinite both',
          },
          '& > div:nth-of-type(1)': {
            animationDelay: '-0.32s',
          },
          '& > div:nth-of-type(2)': {
            animationDelay: '-0.16s',
          },
        }}
      >
        <Box />
        <Box />
        <Box />
      </Box>

      {/* Custom CSS Animations with unique names */}
      <style jsx>{`
        .fancy-backdrop-loader-container {
          isolation: isolate;
        }

        /* Custom animation names to avoid conflicts */
        @keyframes fancyBackdropSpin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        @keyframes fancyBackdropPulse {
          0%,
          100% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }
          50% {
            transform: translate(-50%, -50%) scale(1.3);
            opacity: 0.7;
          }
        }

        @keyframes fancyBackdropOrbit {
          0% {
            transform: translate(-50%, -50%) rotate(0deg) translateX(20px) rotate(0deg);
          }
          100% {
            transform: translate(-50%, -50%) rotate(360deg) translateX(20px) rotate(-360deg);
          }
        }

        @keyframes fancyBackdropBounce {
          0%,
          80%,
          100% {
            transform: scale(0);
          }
          40% {
            transform: scale(1);
          }
        }

        @keyframes fancyBackdropFadeInOut {
          0%,
          100% {
            opacity: 0.7;
          }
          50% {
            opacity: 1;
          }
        }
      `}</style>
    </Box>
  );
};

export default FancyBackdropLoader;
